import { AutocompleteInput, NumberInput, TextInput } from "@/components/admin";
import { useCreate, useGetList } from "ra-core";
import { useWatch } from "react-hook-form";

/**
 * Loose category matcher so the conditional dimension UI keeps working for both
 * the seeded master categories (e.g. "Custom Fabrication") and legacy enquiry
 * records that stored slugs (e.g. "custom_fabrication").
 */
const categoryMatches = (value: string | undefined, key: "custom" | "standard") => {
    if (!value) return false;
    const v = value.toLowerCase();
    return key === "custom" ? v.includes("custom") : v.includes("standard");
};

/**
 * Product Category selector backed by the `product_categories` master table.
 * Enquiry #20: users can pick an existing category or type a new one (onCreate),
 * which is persisted to the master table for reuse across enquiries.
 */
const ProductCategoryInput = () => {
    const [create] = useCreate();
    const { data: categories } = useGetList("product_categories", {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "name", order: "ASC" },
        filter: { is_active: true },
    });

    const choices = (categories ?? []).map((c: any) => ({ id: c.name, name: c.name }));

    return (
        <AutocompleteInput
            source="requirements.product_type"
            label="Product Category"
            choices={choices}
            className="w-full"
            placeholder="Select or type a new category..."
            onCreate={async (value?: string) => {
                const name = value?.trim();
                // Guard against empty / ra-core sentinel values being persisted as a category.
                if (!name || name.startsWith("@@")) return undefined;
                // Reuse an existing category if the typed name already exists.
                const existing = choices.find(
                    (c) => c.name.toLowerCase() === name.toLowerCase(),
                );
                if (existing) return existing;
                await create(
                    "product_categories",
                    { data: { name } },
                    { returnPromise: true },
                ).catch(() => undefined);
                return { id: name, name };
            }}
        />
    );
};

export const ProductRequirements = () => {
    const productType = useWatch({ name: "requirements.product_type" });

    return (
        <div className="space-y-4 w-full md:col-span-3">
            <ProductCategoryInput />

            {categoryMatches(productType, "custom") && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded border col-span-full">
                    <span className="col-span-full text-xs font-bold text-slate-500 uppercase">Dimensions (Numeric)</span>
                    <NumberInput source="requirements.length" label="Length" min={0} className="w-full" />
                    <NumberInput source="requirements.width" label="Width" min={0} className="w-full" />
                    <NumberInput source="requirements.height" label="Height" min={0} className="w-full" />
                    <TextInput source="requirements.materials" label="Material Preferences" className="col-span-full" />
                </div>
            )}

            {categoryMatches(productType, "standard") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded border col-span-full">
                    <TextInput source="requirements.dimensions" label="Size / Variant" className="w-full" />
                    <NumberInput source="requirements.quantity" label="Estimated Quantity" min={1} className="w-full" />
                </div>
            )}

            <TextInput
                source="requirements.special_notes"
                label="Special Requirements & Notes"
                multiline
                rows={4}
                className="w-full max-w-none col-span-full mt-4"
            />
        </div>
    );
};
